import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { QuantApiService } from './quant-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { RoutingService } from 'src/app/service/routing.service';
import { ParamsBuilder } from '../../utils/params-builder';
import { QueryParams } from '../../models/query-params.interface';

export type PriceDataType = 'price' | 'price_delta' | 'volatility';

export interface YlPricePoint {
  date: string;
  price: number;
}

export interface YlPriceResponse {
  prices: YlPricePoint[];
  currency: string | string[];
}

export interface PriceDataItemizedEntry {
  values?: { value: number | null }[];
  currency?: string;
}

// Legacy yl_price strips lang/feed_types (and never sends period).
const YL_PRICE_PARAMS = [
  'from',
  'to',
  'categories',
  'continents',
  'countries',
  'factuality',
  'temporality',
  'ranks',
  'panels',
] as const;

// Price endpoints are best-effort decorations; the legacy path capped them at 2s.
const PRICE_TIMEOUT_MS = 2000;

/**
 * Quant trading price data. Replaces the legacy `yukkApi.scoresTimeSeries`
 * `yl_price` / `price` / `price_delta` / `volatility` branches.
 */
@Injectable({
  providedIn: 'root',
})
export class PriceDataService {
  constructor(
    private quantApiService: QuantApiService,
    private config: ConfigService,
    private routing: RoutingService,
  ) {}

  /** GET `trading/{type}/{id}/yl_price`; errors/timeouts degrade to empty prices. */
  getYlPrice(
    entityType: string,
    entityId: string,
    queryParams: QueryParams,
  ): Observable<YlPriceResponse> {
    // Legacy yl_price ignored `newsday` — strip it so ParamsBuilder cannot
    // narrow from/to to one day (price-change needs prices.length > 1).
    const iparams = { ...queryParams };
    delete iparams.newsday;
    const params = ParamsBuilder.build(
      iparams,
      YL_PRICE_PARAMS,
      this.config.appConfig.routing.apiproj,
      { isReader: this.routing.isReader() || this.routing.isStoryReader() },
    );
    return this.quantApiService
      .get<YlPriceResponse>(
        `${entityType}/${entityId}/yl_price`,
        params,
        'trading',
      )
      .pipe(
        timeout(PRICE_TIMEOUT_MS),
        map((resp) => JSON.parse(JSON.stringify(resp))),
        catchError(() => of({ prices: [], currency: [] })),
      );
  }

  /**
   * POST `trading/portfolio/price_data_itemized?type={type}`; returns one entry
   * per posted entity. Errors/timeouts degrade to null-value placeholders.
   */
  getPriceDataItemized(
    type: PriceDataType,
    portfolioContent: string[],
    queryParams: QueryParams,
  ): Observable<PriceDataItemizedEntry[]> {
    if (!portfolioContent.length) {
      return of([]);
    }
    const params = {
      ...ParamsBuilder.build(
        queryParams,
        ['period', ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS],
        this.config.appConfig.routing.apiproj,
        { isReader: this.routing.isReader() || this.routing.isStoryReader() },
      ),
      type,
    };
    return this.quantApiService
      .post<{
        price_data_itemized: PriceDataItemizedEntry[];
      }>('portfolio/price_data_itemized', portfolioContent, params, 'trading')
      .pipe(
        timeout(PRICE_TIMEOUT_MS),
        map((resp) => JSON.parse(JSON.stringify(resp.price_data_itemized))),
        catchError(() =>
          of(portfolioContent.map(() => ({ values: [{ value: null }] }))),
        ),
      );
  }
}
